import axios from 'axios';
import type {
  AnnounceNotificationBody,
  AnnounceResult,
  NotificationAudiences,
  NotificationBatchDetail,
  NotificationBatchDto,
  NotificationBulkBody,
  NotificationCategory,
  NotificationDto,
  NotificationListResponse,
  NotificationPreferences,
  NotificationPriority,
  NotificationRecipient,
  NotificationSummary,
} from '@nexops/shared';
import { api } from '@/shared/lib/api';

/** Query keys — everything lives under `['notifications', kind, …]` so one invalidation refreshes the whole feature. */
export const notifKeys = {
  root: ['notifications'] as const,
  list: (f: InboxQuery) => ['notifications', 'list', f] as const,
  overview: ['notifications', 'overview'] as const,
  bell: ['notifications', 'bell'] as const,
  summary: ['notifications', 'summary'] as const,
  prefs: ['notifications', 'preferences'] as const,
  audiences: ['notifications', 'audiences'] as const,
  recipients: (search: string) => ['notifications', 'recipients', search] as const,
  sent: (page: number) => ['notifications', 'sent', page] as const,
  batch: (id: string) => ['notifications', 'batch', id] as const,
};

/** Kinds of cached queries holding `NotificationDto` rows (patched optimistically). */
export const ROW_KINDS = ['list', 'overview', 'bell'] as const;

export interface InboxQuery {
  category?: NotificationCategory;
  priority?: NotificationPriority;
  unreadOnly?: boolean;
  pinned?: boolean;
  search?: string;
}

export const INBOX_PAGE_SIZE = 50;

export const notificationsApi = {
  list: (q: InboxQuery, page: number, pageSize = INBOX_PAGE_SIZE) =>
    api.get<NotificationListResponse>('/notifications', {
      category: q.category,
      priority: q.priority,
      unreadOnly: q.unreadOnly ? true : undefined,
      pinned: q.pinned ? true : undefined,
      search: q.search || undefined,
      page,
      pageSize,
    }),
  summary: () => api.get<NotificationSummary>('/notifications/summary'),
  readAll: (category?: NotificationCategory) => api.post<{ updated: number }>(`/notifications/read-all${category ? `?category=${category}` : ''}`),
  read: (id: string) => api.post<{ id: string; read: true }>(`/notifications/${id}/read`),
  unread: (id: string) => api.post<{ id: string; read: false }>(`/notifications/${id}/unread`),
  pin: (id: string, pinned: boolean) => api.post<NotificationDto>(`/notifications/${id}/pin`, { pinned }),
  confirm: (id: string) => api.post<NotificationDto>(`/notifications/${id}/confirm`),
  remove: (id: string) => api.delete<{ id: string; deleted: true }>(`/notifications/${id}`),
  bulk: (body: NotificationBulkBody) => api.post<{ updated: number }>('/notifications/bulk', body),
  getPrefs: () => api.get<NotificationPreferences>('/notifications/preferences'),
  putPrefs: (p: NotificationPreferences) => api.put<NotificationPreferences>('/notifications/preferences', p),
  audiences: () => api.get<NotificationAudiences>('/notifications/audiences'),
  recipients: (search: string) => api.get<NotificationRecipient[]>('/notifications/recipients', { search: search || undefined }),
  announce: (body: AnnounceNotificationBody) => api.post<AnnounceResult>('/notifications/announce', body),
  sent: (page: number, pageSize = 20) =>
    api.get<{ items: NotificationBatchDto[]; total: number; page: number; pageSize: number }>('/notifications/sent', { page, pageSize }),
  batch: (id: string) => api.get<NotificationBatchDetail>(`/notifications/sent/${id}`),
};

/** Per-field validation messages from a Nest/class-validator 400 (`message: ["title must be …", …]`). */
export function apiFieldErrors(error: unknown, fields: string[]): { byField: Record<string, string>; general?: string } {
  if (!axios.isAxiosError(error)) return { byField: {} };
  const raw = (error.response?.data as { message?: string | string[] } | undefined)?.message;
  const messages = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const byField: Record<string, string> = {};
  const rest: string[] = [];
  for (const m of messages) {
    const field = fields.find((f) => m.toLowerCase().startsWith(f.toLowerCase()));
    if (field && !byField[field]) byField[field] = m;
    else rest.push(m);
  }
  return { byField, general: rest.length ? rest.join(', ') : undefined };
}
